// Thumbnail scheduler: limited concurrency, visible thumbs first, off-screen requests cancelled,
// back-off on 429, and an LRU of object URLs so remounted rows don't refetch.
const MAX_ACTIVE = 6;
const MAX_CACHED = 800;
const cache = new Map(); // url -> objectURL
const waiting = new Map(); // url -> { prio, listeners:Set }
const active = new Map(); // url -> { ctrl, w }
let pausedUntil = 0;
let timer = null;

const remember = (url, obj) => {
  cache.set(url, obj);
  if (cache.size > MAX_CACHED) {
    const [old, oldObj] = cache.entries().next().value;
    cache.delete(old);
    URL.revokeObjectURL(oldObj);
  }
};

const pump = () => {
  const now = Date.now();
  if (now < pausedUntil) {
    clearTimeout(timer);
    timer = setTimeout(pump, pausedUntil - now);
    return;
  }
  while (active.size < MAX_ACTIVE && waiting.size) {
    let best = null;
    waiting.forEach((w, url) => { if (!best || w.prio > best[1].prio) best = [url, w]; });
    const [url, w] = best;
    waiting.delete(url);
    const entry = { ctrl: new AbortController(), w };
    active.set(url, entry);
    fetch(url, { credentials: "include", signal: entry.ctrl.signal })
      .then((r) => {
        if (r.status === 429) {
          pausedUntil = Date.now() + 2500;
          throw Object.assign(new Error("rate"), { retry: true });
        }
        if (!r.ok) throw new Error(String(r.status));
        return r.blob();
      })
      .then((b) => {
        const obj = URL.createObjectURL(b);
        remember(url, obj);
        w.listeners.forEach((fn) => fn(obj));
      })
      .catch((e) => {
        if (e.retry && w.listeners.size) waiting.set(url, w);
        else if (e.name !== "AbortError") w.listeners.forEach((fn) => fn(null));
      })
      .finally(() => { if (active.get(url) === entry) active.delete(url); pump(); });
  }
};

export const thumbCache = (url) => cache.get(url);

// Returns an unsubscribe fn. `prio`: 2 = visible, 1 = in overscan buffer.
// A cell that remounts while its fetch is in flight joins that fetch; if it was already aborted, it's re-queued.
export function requestThumb(url, prio, onLoad) {
  if (cache.has(url)) {
    onLoad(cache.get(url));
    return () => {};
  }
  const a = active.get(url);
  const live = a && !a.ctrl.signal.aborted;
  const w = waiting.get(url) || (live ? a.w : null) || { prio, listeners: new Set() };
  w.prio = Math.max(w.prio, prio);
  w.listeners.add(onLoad);
  if (!live) waiting.set(url, w);
  pump();
  return () => {
    w.listeners.delete(onLoad);
    if (w.listeners.size) return;
    if (waiting.get(url) === w) waiting.delete(url);
    const cur = active.get(url);
    if (cur && cur.w === w) cur.ctrl.abort();
  };
}
